import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Printer, Loader, FileText, Grid, Table2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateWorkoutPDF } from './generateWorkoutPDF';
import { generateWorkoutPDFGrid } from './generateWorkoutPDFGrid';
import { generateWorkoutPDFTable } from './generateWorkoutPDFTable';

const PDFPreviewModal = ({ isOpen, onClose, workout, athlete, gymLogo, gymName }) => {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [templateType, setTemplateType] = useState('standard'); // 'standard' | 'grid' | 'table'

    const generateByTemplate = async (apiBaseUrl) => {
        if (templateType === 'grid') {
            return generateWorkoutPDFGrid(workout, athlete, gymLogo, gymName, apiBaseUrl);
        }
        if (templateType === 'table') {
            return generateWorkoutPDFTable(workout, athlete, gymLogo, gymName, apiBaseUrl);
        }
        return generateWorkoutPDF(workout, athlete, gymLogo, gymName, apiBaseUrl);
    };

    useEffect(() => {
        if (isOpen && workout) {
            generatePDF();
        }
        
        // Cleanup
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [isOpen, workout, templateType]);

    const generatePDF = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Costruisci apiBaseUrl (stessa logica usata in api.js)
            let apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
            if (!apiBaseUrl) {
                // Se non c'è REACT_APP_API_BASE_URL, usa window.location.origin se disponibile
                if (window.location.origin && !window.location.origin.includes('localhost')) {
                    apiBaseUrl = window.location.origin + '/api';
                } else {
                    // Fallback all'IP del server (sviluppo o localhost)
                    apiBaseUrl = 'http://192.168.1.113/api';
                }
            }
            
            const doc = await generateByTemplate(apiBaseUrl);

            // Converti in blob e crea URL
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
        } catch (err) {
            console.error('Errore generazione PDF:', err);
            setError('Errore nella generazione del PDF. Riprova.');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        try {
            // Costruisci apiBaseUrl (stessa logica usata in api.js)
            let apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
            if (!apiBaseUrl) {
                // Se non c'è REACT_APP_API_BASE_URL, usa window.location.origin se disponibile
                if (window.location.origin && !window.location.origin.includes('localhost')) {
                    apiBaseUrl = window.location.origin + '/api';
                } else {
                    // Fallback all'IP del server (sviluppo o localhost)
                    apiBaseUrl = 'http://192.168.1.113/api';
                }
            }
            
            const doc = await generateByTemplate(apiBaseUrl);
            const fileName = `Scheda_${athlete.name.replace(/\s+/g, '_')}_${workout.nome.replace(/\s+/g, '_')}.pdf`;
            doc.save(fileName);
        } catch (err) {
            console.error('Errore download PDF:', err);
            toast.error('Errore nel download del PDF');
        }
    };

    const handlePrint = () => {
        if (pdfUrl) {
            // Apri PDF in nuova finestra per stampare
            const printWindow = window.open(pdfUrl, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            }
        }
    };

    if (!isOpen) return null;

    const modal = (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="flex h-[90vh] max-h-[90vh] w-[90vw] max-w-[90vw] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
                {/* Header */}
                <div className="shrink-0 border-b border-gray-200 bg-[#dad8e5] p-6 dark:border-white/10 dark:bg-gray-800">
                    <div className="mb-4 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                            <h2 className="text-2xl font-bold text-gray-900 dark:font-open-sans dark:text-white">
                                Anteprima Scheda Allenamento
                            </h2>
                            <p className="mt-1 text-gray-600 dark:text-white/75">
                                {athlete.name} - {workout.nome}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="shrink-0 rounded-lg p-2 text-gray-600 transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
                        >
                            <X size={24} />
                        </button>
                    </div>
                    
                    {/* Selettore Template */}
                    <div className="flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={() => setTemplateType('standard')}
                            className={`flex items-center gap-2 rounded-lg px-4 py-2 transition-colors ${
                                templateType === 'standard'
                                    ? 'bg-blue-600 text-white dark:bg-indigo-600 dark:text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-800 dark:text-white dark:hover:brightness-110'
                            }`}
                        >
                            <FileText size={18} />
                            Template Standard
                        </button>
                        <button
                            type="button"
                            onClick={() => setTemplateType('grid')}
                            className={`flex items-center gap-2 rounded-lg px-4 py-2 transition-colors ${
                                templateType === 'grid'
                                    ? 'bg-blue-600 text-white dark:bg-indigo-600 dark:text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-800 dark:text-white dark:hover:brightness-110'
                            }`}
                        >
                            <Grid size={18} />
                            Template Griglia
                        </button>
                        <button
                            type="button"
                            onClick={() => setTemplateType('table')}
                            className={`flex items-center gap-2 rounded-lg px-4 py-2 transition-colors ${
                                templateType === 'table'
                                    ? 'bg-blue-600 text-white dark:bg-indigo-600 dark:text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-800 dark:text-white dark:hover:brightness-110'
                            }`}
                        >
                            <Table2 size={18} />
                            Template Tabella
                        </button>
                    </div>
                </div>

                {/* PDF Preview */}
                <div className="relative flex-1 overflow-hidden bg-gray-100 dark:bg-gray-900">
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-900">
                            <div className="text-center">
                                <Loader className="animate-spin mx-auto mb-4 text-blue-600" size={48} />
                                <p className="text-gray-600 dark:text-white/75">Generazione PDF in corso...</p>
                            </div>
                        </div>
                    )}
                    
                    {error && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-900">
                            <div className="text-center text-red-600">
                                <p className="font-semibold mb-2">❌ {error}</p>
                                <button
                                    onClick={generatePDF}
                                    className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                                >
                                    Riprova
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {pdfUrl && !loading && !error && (
                        <iframe
                            src={pdfUrl}
                            className="w-full h-full"
                            title="Anteprima PDF"
                        />
                    )}
                </div>

                {/* Footer Actions */}
                <div className="shrink-0 border-t border-gray-200 bg-gray-50 p-6 dark:border-white/10 dark:bg-gray-800">
                    <div className="flex flex-wrap justify-end gap-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg border border-gray-300 px-6 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-white/20 dark:bg-gray-800 dark:text-white dark:hover:brightness-110"
                        >
                            Chiudi
                        </button>
                        
                        <button
                            type="button"
                            onClick={handleDownload}
                            disabled={loading || error}
                            className="flex items-center gap-2 rounded-lg bg-green-600 px-6 py-2 font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-950 dark:text-gray-300 dark:hover:brightness-110 dark:disabled:opacity-50"
                        >
                            <Download size={18} />
                            Scarica PDF
                        </button>

                        <button
                            type="button"
                            onClick={handlePrint}
                            disabled={loading || error}
                            className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110 dark:disabled:opacity-50"
                        >
                            <Printer size={18} />
                            Stampa
                        </button>
                    </div>
                    
                    <p className="mt-4 text-center text-sm text-gray-500 dark:text-white/70">
                        💡 Suggerimento: Usa "Scarica PDF" per salvare o "Stampa" per stampare direttamente
                    </p>
                </div>
            </div>
        </div>
    );

    if (typeof document === 'undefined') return modal;
    return createPortal(modal, document.body);
};

export default PDFPreviewModal;
